import {
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  query,
  orderByChild,
  equalTo,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { Booking } from '../../types';
import { dateToMs, msToDate, snapshotToArray, stripUndefined } from './helpers';

const PATH = 'bookings';

type Unsubscribe = () => void;

const fromRtdb = (id: string, data: any): Booking => ({
  ...data,
  id,
  startDate: msToDate(data.startDate) as Date,
  endDate: msToDate(data.endDate) as Date,
});

const toRtdb = (booking: Booking): any => {
  const { id: _id, ...rest } = booking;
  return {
    ...rest,
    startDate: dateToMs(booking.startDate),
    endDate: dateToMs(booking.endDate),
  };
};

const sortByStartDateDesc = (bookings: Booking[]): Booking[] =>
  [...bookings].sort((a, b) => b.startDate.getTime() - a.startDate.getTime());

export const getAllBookings = async (): Promise<Booking[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return sortByStartDateDesc(
    snapshotToArray<Booking>(snapshot.val(), fromRtdb)
  );
};

export const getBookingById = async (
  bookingId: string
): Promise<Booking | null> => {
  const snapshot = await get(ref(rtdb, `${PATH}/${bookingId}`));
  return snapshot.exists() ? fromRtdb(bookingId, snapshot.val()) : null;
};

export const saveBooking = async (booking: Booking): Promise<void> => {
  await set(
    ref(rtdb, `${PATH}/${booking.id}`),
    stripUndefined(toRtdb(booking))
  );
};

export const updateBooking = async (
  bookingId: string,
  updates: Partial<Booking>
): Promise<void> => {
  const data: any = { ...updates };
  if (updates.startDate !== undefined) {
    data.startDate = dateToMs(updates.startDate);
  }
  if (updates.endDate !== undefined) {
    data.endDate = dateToMs(updates.endDate);
  }
  await update(ref(rtdb, `${PATH}/${bookingId}`), stripUndefined(data));
};

export const deleteBooking = async (bookingId: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${bookingId}`));
};

export const getBookingsByHomeId = async (
  homeId: string
): Promise<Booking[]> => {
  const q = query(ref(rtdb, PATH), orderByChild('homeId'), equalTo(homeId));
  const snapshot = await get(q);
  return sortByStartDateDesc(
    snapshotToArray<Booking>(snapshot.val(), fromRtdb)
  );
};

export const getBookingsByUserId = async (
  userId: string
): Promise<Booking[]> => {
  const q = query(ref(rtdb, PATH), orderByChild('userId'), equalTo(userId));
  const snapshot = await get(q);
  return sortByStartDateDesc(
    snapshotToArray<Booking>(snapshot.val(), fromRtdb)
  );
};

export const getBookingsByStatus = async (
  status: 'pending' | 'approved' | 'cancelled'
): Promise<Booking[]> => {
  const q = query(ref(rtdb, PATH), orderByChild('status'), equalTo(status));
  const snapshot = await get(q);
  return sortByStartDateDesc(
    snapshotToArray<Booking>(snapshot.val(), fromRtdb)
  );
};

export const subscribeToBookings = (
  callback: (bookings: Booking[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(
        sortByStartDateDesc(snapshotToArray<Booking>(snapshot.val(), fromRtdb))
      );
    },
    onError
  );
  return () => off(r, 'value', handler);
};

export const subscribeToHomeBookings = (
  homeId: string,
  callback: (bookings: Booking[]) => void
): Unsubscribe => {
  const q = query(ref(rtdb, PATH), orderByChild('homeId'), equalTo(homeId));
  const handler = onValue(q, (snapshot) => {
    callback(
      sortByStartDateDesc(snapshotToArray<Booking>(snapshot.val(), fromRtdb))
    );
  });
  return () => off(q, 'value', handler);
};

export const subscribeToUserBookings = (
  userId: string,
  callback: (bookings: Booking[]) => void
): Unsubscribe => {
  const q = query(ref(rtdb, PATH), orderByChild('userId'), equalTo(userId));
  const handler = onValue(q, (snapshot) => {
    callback(
      sortByStartDateDesc(snapshotToArray<Booking>(snapshot.val(), fromRtdb))
    );
  });
  return () => off(q, 'value', handler);
};

const datesOverlap = (
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean => {
  return start1 < end2 && end1 > start2;
};

/**
 * Check availability for a home during specified dates.
 * Loads all bookings for the home and filters in-memory for pending/approved
 * (RTDB cannot do compound where clauses).
 */
export const checkAvailability = async (
  homeId: string,
  startDate: Date,
  endDate: Date,
  excludeBookingId?: string
): Promise<{ available: boolean; conflictingBookings: Booking[] }> => {
  const q = query(ref(rtdb, PATH), orderByChild('homeId'), equalTo(homeId));
  const snapshot = await get(q);
  const bookings = snapshotToArray<Booking>(snapshot.val(), fromRtdb)
    .filter((b) => b.status === 'pending' || b.status === 'approved')
    .filter((b) => b.id !== excludeBookingId);

  const conflictingBookings = bookings.filter((booking) =>
    datesOverlap(startDate, endDate, booking.startDate, booking.endDate)
  );

  return {
    available: conflictingBookings.length === 0,
    conflictingBookings,
  };
};

/**
 * Check if dates conflict with blocked date ranges on the home
 */
export const checkBlockedDates = (
  blockedRanges: Array<{ startDate: Date; endDate: Date; reason: string }>,
  startDate: Date,
  endDate: Date
): { blocked: boolean; reason?: string } => {
  for (const range of blockedRanges) {
    if (datesOverlap(startDate, endDate, range.startDate, range.endDate)) {
      return { blocked: true, reason: range.reason };
    }
  }
  return { blocked: false };
};
